import { defineDirective, IOmniaShell } from "../../../omnia"
import { ISoilService } from "../../../types"
import { AllocationHistoryAction, MapAllocationContext } from "../context"
import { AnalysisType, IAnalysisType } from "../types"
import { GlobalAnalysisAlloc } from "../viewmodel/alloc-vm"
import { GlobalAnalysisVM } from "../viewmodel/analysis-vm"
import { GlobalHealthySoilsAssessmentVM } from "../viewmodel/hsa-vm"
import { GlobalNitrogenAnalysisVM } from "../viewmodel/nitrogen-vm"
import { GlobalSoilAnalysisVM } from "../viewmodel/soilanalysis-vm"

export class MapAllocationsCtrl {

    static create(): ng.IDirective {
        return defineDirective(MapAllocationsCtrl, "app/soil/dashboard/global-map/allocation/map-allocation.html", {
            context: "="
        });
    }

    public context: MapAllocationContext;

    private _selectedAnalysis: GlobalAnalysisVM;
    private _analysisType: IAnalysisType;

    public analysis: GlobalAnalysisVM[];
    public filtredAnalysis: GlobalAnalysisVM[];
    public types: IAnalysisType[];

    private _search: string;
    private _showOnlyUnalloc: boolean;

    public ready: boolean;

    static $inject = ["$scope", "OmniaSoilService", "OmniaShell"];

    constructor(private $scope: ng.IScope, private svc: ISoilService, private shell: IOmniaShell) {

    }

    $onInit() {

        const { $scope } = this

        $scope.$watch("vm.context", () => this.setup());

        $scope.$watch("vm.context.selectedAnalysis",
            () => {
                if (this.context && this.context.selectedAnalysis !== this.selectedAnalysis) {
                    this.selectedAnalysis = this.context.selectedAnalysis;
                }
            });

    }

    get layerOpacity(): number {
        return this.context ? this.context.layerOpacity : 100;
    }

    set layerOpacity(value: number) {
        if (this.context) {
            this.context.layerOpacity = value;
        }
    }

    get search(): string {
        return this._search;
    }

    set search(value: string) {
        this._search = value;
        this.applyFilters();
    }

    get showOnlyUnalloc(): boolean {
        return this._showOnlyUnalloc;
    }

    set showOnlyUnalloc(value: boolean) {
        this._showOnlyUnalloc = value;
        this.applyFilters();
    }

    get showOnlyFields(): boolean {
        return this.context.showOnlyFields;
    }

    set showOnlyFields(value: boolean) {
        this.context.showOnlyFields = value;
        this.context.setupLayers();
    }

    get showOnlyFieldFilter(): boolean {
        return this.analysisType && this.analysisType.type === AnalysisType.Soil;
    }

    private setup() {
        if (this.context) {
            this.types = [
                { name: "Soil analysis", type: AnalysisType.Soil },
                { name: "Nitrogen analysis", type: AnalysisType.Nitrogen },
                { name: "Healthy soils assessment", type: AnalysisType.HSA }
            ];

            this.analysisType = this.types[0];

            this.ready = true;
        }
    }

    private applyFilters() {

        var analysis = this.analysis;

        if (this.showOnlyUnalloc) {
            analysis = analysis.filter(a => a.allocations.length === 0);
        }

        if (this.search && this.search.trim()) {
            var search = this.search.trim().toLowerCase();
            analysis = analysis.filter(a => a.getDescription().toLowerCase().indexOf(search) !== -1);
        }

        this.filtredAnalysis = analysis;
    }

    get changed(): boolean {
        if (!this.analysis) return false;
        return this.analysis.filter(a => a.allocationsChanged()).length > 0;
    }

    get analysisType(): IAnalysisType {
        return this._analysisType;
    }

    set analysisType(value: IAnalysisType) {
        this._analysisType = value;
        this.setupType();
    }

    get selectedAnalysis(): GlobalAnalysisVM {
        return this._selectedAnalysis;
    }

    set selectedAnalysis(value: GlobalAnalysisVM) {
        this._selectedAnalysis = value;
        this.setupAnalysis();
    }

    private setupType() {

        if (this.analysisType.type === AnalysisType.Soil) {

            this.analysis = this.svc.getSoilAnalyses()
                .filter(a => a.FirstYear <= this.svc.yearId)
                .map(a => new GlobalSoilAnalysisVM(this.svc, a));

        } else if (this.analysisType.type === AnalysisType.Nitrogen) {

            this.analysis = this.svc.getNitrogenAnalysis().filter(a => a.SamplingDate.getFullYear() <= this.svc.yearId)
                .map(a => new GlobalNitrogenAnalysisVM(this.svc, a));

        } else if (this.analysisType.type === AnalysisType.HSA) {

            this.analysis = this.svc.getHealthySoilsAssessments().filter(a => a.FirstYearId <= this.svc.yearId)
                .map(a => new GlobalHealthySoilsAssessmentVM(this.svc, a));

        }

        this.context.analysis = this.analysis;
        this.context.analysisType = this.analysisType.type;

        this.applyFilters();
    }

    private setupAnalysis() {

        this.context.selectedAnalysis = this.selectedAnalysis;

    }

    private canUndo(): boolean {
        // 170407 - Disabled for now
        return false;
        //return !!this.context.allocationHistory && this.context.allocationHistory.length > 0;
    }

    get undoMsg(): string {
        if (!this.canUndo()) return "Nothing to undo yet.";

        var allocHistory = this.context.allocationHistory[this.context.allocationHistory.length - 1];
        return "Undo '" +
            (allocHistory.action === AllocationHistoryAction.Allocate ? "Allocate" : "Unallocate") +
            " " +
            allocHistory.analysis.getDescription() +
            " from " +
            allocHistory.layerData.field.getDescription(true) +
            "'";
    }

    private undo() {

        var allocHistory = this.context.allocationHistory.pop();

        if (allocHistory.action === AllocationHistoryAction.Unallocate) {

            allocHistory.analysis.alloc(allocHistory.layerData);
            allocHistory.layerData.analysis = allocHistory.analysis;

        } else {

            allocHistory.analysis.unallocFeature(allocHistory.layerData);
            allocHistory.layerData.analysis = null;

        }

        this.context.refreshFeatures([allocHistory.layerData]);
        this.context.refreshFeatureSelection(allocHistory.layerData);
    }

    private cancelAnalysisChanges() {

        var datas = [];

        for (var i = this.context.allocationHistory.length - 1; i >= 0; i--) {
            var allocHistory = this.context.allocationHistory[i];

            if (datas.indexOf(allocHistory.layerData) === -1) {
                datas.push(allocHistory.layerData);
            }

            if (allocHistory.action === AllocationHistoryAction.Unallocate) {

                allocHistory.analysis.alloc(allocHistory.layerData);
                allocHistory.layerData.analysis = allocHistory.analysis;

            } else {

                allocHistory.analysis.unallocFeature(allocHistory.layerData);
                allocHistory.layerData.analysis = null;

            }
        }

        this.context.allocationHistory = [];

        this.context.refreshFeatures(datas);

        this.selectedAnalysis = null;
    }

    private commitAnalysisChanges() {
        this.context.allocationHistory = [];
        this.selectedAnalysis = null;
    }


    private unalloc(alloc: GlobalAnalysisAlloc) {

        alloc.layerData.analysis = null;

        this.context.allocationHistory.push({
            action: AllocationHistoryAction.Unallocate,
            analysis: this.selectedAnalysis,
            layerData: alloc.layerData
        });

        this.selectedAnalysis.unallocFeature(alloc.layerData);

        this.context.refreshFeatureSelection(alloc.layerData);
    }
}
